import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X as CloseIcon, UserPlus, Bot, Trophy, Heart } from 'lucide-react';

import { Avatar, Pill, Btn } from './';
import { ReportDialog } from './ReportDialog';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import {
  sendFriendRequest, declineOrRemoveFriend, FriendsError, ATOM_UID,
  myFriendshipsQuery, mapFriendships, statusWith,
} from '../../services/friendsService';
import { blockUser, SafetyError } from '../../services/safetyService';
import { useLiveQuery } from '../../hooks/useLiveQuery';
import { useToast } from './Toast';

/**
 * Quick profile preview modal. Opens when you tap any avatar/name in
 * the lobby, game, or game-results — small enough to keep the game
 * visible behind it (chess.com behavior). Shows W/L record, bio,
 * and a one-tap friend-action button. Tapping "View full profile"
 * navigates to /u/:uid.
 *
 * Designed to be lightweight: a single Firestore read for the profile
 * on open, and a friendship lookup. No game-history fetch — that
 * happens on the full profile page.
 */
export interface ProfilePreviewProps {
  uid: string | null;
  onClose: () => void;
  /**
   * Where this preview was opened from. Carried into a report so whoever reads
   * the queue knows which game/thread to go and look at — a report with no
   * context is nearly unactionable.
   */
  contextType?: 'game' | 'thread' | 'name' | 'profile';
  contextId?: string | null;
}

interface Profile {
  uid: string;
  displayName: string;
  avatar: string;
  bio?: string;
  accountType?: 'human' | 'ai' | 'test';
  isDemo?: boolean;
  stats?: { totalGames?: number; totalWins?: number };
}

export function ProfilePreview({
  uid, onClose, contextType = 'profile', contextId = null,
}: ProfilePreviewProps) {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const toast = useToast();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [confirmBlock, setConfirmBlock] = useState(false);

  /**
   * LIVE friendship status.
   *
   * This sheet is opened straight off a game roster, so it is the highest-traffic
   * place in the app to press Add — and it was reading the friendship list ONCE.
   * The other person accepting did not change the button; it went on saying
   * "Requested" until the page was reloaded, which from the sender's chair is
   * indistinguishable from the request never having been sent. That exact
   * complaint ("clicking add friend doesnt really do anything really") is what
   * this file's service layer was rewritten for; the last one-shot read was still
   * here, in the UI.
   *
   * The mutations below no longer set the status either — the listener does, so
   * what you see is what Firestore says, not what we hoped it would say.
   */
  const myUid = currentUser?.uid || '';
  const friendships = useLiveQuery(myFriendshipsQuery(myUid), mapFriendships(myUid));
  const friendStatus = statusWith(friendships.data, myUid, uid || '');

  useEffect(() => {
    let cancelled = false;
    if (!uid || !db) {
      setProfile(null);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        if (cancelled) return;
        if (snap.exists()) {
          const data = snap.data() as any;
          setProfile({
            uid,
            displayName: data.displayName || 'Player',
            avatar: data.avatar || 'fox',
            bio: data.bio || '',
            accountType: data.accountType,
            isDemo: !!data.isDemo,
            stats: data.stats,
          });
        }
      } catch (e) {
        console.warn('[ProfilePreview] load failed', (e as any)?.message || e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uid, currentUser?.uid]);

  if (!uid) return null;

  const isAI = profile?.accountType === 'ai' || uid === ATOM_UID;
  const wins = profile?.stats?.totalWins ?? 0;
  const games = profile?.stats?.totalGames ?? 0;

  const handleAddFriend = async () => {
    if (!currentUser || !profile || busy) return;
    setBusy(true);
    try {
      // `userProfile.avatar` — NOT currentUser.photoURL, which is a Google image
      // URL and not an avatar id at all. It used to be passed straight into the
      // avatar slot of the denormalized profile map, so every friendship this
      // modal created carried a broken avatar for the sender; when photoURL was
      // absent it was `undefined`, which Firestore rejects outright — an
      // invalid-data throw, caught here, logged to a console nobody was reading.
      // That was one of the ways "Add friend" genuinely did nothing.
      const outcome = await sendFriendRequest(
        currentUser.uid,
        profile.uid,
        userProfile?.displayName || currentUser.displayName || 'Player',
        userProfile?.avatar || 'fox',
        profile.displayName,
        profile.avatar,
      );
      toast.success(
        outcome === 'sent' ? 'Request sent.'
          : outcome === 'already-sent' ? 'You already asked — still waiting on them.'
            : outcome === 'accepted-incoming' ? "They'd asked you too. You're friends now."
              : "You're already friends.",
      );
    } catch (e) {
      toast.error(e instanceof FriendsError ? e.userMessage : "We couldn't send that request.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveFriend = async () => {
    if (!currentUser || !profile || busy) return;
    setBusy(true);
    try {
      await declineOrRemoveFriend(currentUser.uid, profile.uid);
      toast.info('Friend removed.');
    } catch (e) {
      toast.error(e instanceof FriendsError ? e.userMessage : "We couldn't do that.");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Block, with one confirmation step. Not because blocking is dangerous — it
   * is reversible from Settings — but because it is the only action in this
   * sheet that changes who your children can meet, and a mis-tap next to
   * "Add as friend" should not do it silently.
   */
  const handleBlock = async () => {
    if (!currentUser || !profile || busy) return;
    setBusy(true);
    try {
      await blockUser(profile.uid);
      // A block implies you no longer want them in your list either. Leaving the
      // friendship intact would keep them in your Friends tab, able to invite
      // you, which is not what anybody means by "block".
      await declineOrRemoveFriend(currentUser.uid, profile.uid).catch(() => {});
      toast.success(`${profile.displayName} is blocked. They can't join your games.`);
      onClose();
    } catch (e) {
      toast.error(e instanceof SafetyError ? e.userMessage : "We couldn't block them.");
    } finally {
      setBusy(false);
      setConfirmBlock(false);
    }
  };

  /** Blocking or reporting Atom, yourself, or a demo account is meaningless. */
  const canModerate = !!currentUser && !isAI && !profile?.isDemo && friendStatus !== 'self';

  return (
    <AnimatePresence>
      <motion.div
        key="backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label="Profile preview"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 220,
          background: 'rgba(0,0,0,0.32)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <motion.div
          key="card"
          initial={{ y: 16, opacity: 0, scale: 0.96 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 8, opacity: 0, scale: 0.97 }}
          transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          onClick={e => e.stopPropagation()}
          className="card lg"
          style={{
            width: '100%',
            maxWidth: 360,
            background: 'var(--surface)',
            padding: 20,
            position: 'relative',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              position: 'absolute', top: 10, right: 10,
              width: 28, height: 28, borderRadius: 8,
              border: 'none', background: 'rgba(0,0,0,0.04)',
              cursor: 'pointer', color: 'var(--ink-2)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <CloseIcon size={14} />
          </button>

          {loading ? (
            <div className="wr-col gap-2 wr-center" style={{ padding: 24 }}>
              <div className="wr-skel" style={{ width: 64, height: 64, borderRadius: '50%' }} />
              <div className="wr-skel" style={{ width: 120, height: 14, borderRadius: 6 }} />
            </div>
          ) : !profile ? (
            <div className="wr-col gap-2 wr-center" style={{ padding: 24 }}>
              <div className="wr-text-3" style={{ fontSize: 13 }}>Player not found</div>
            </div>
          ) : (
            <>
              <div className="wr-row gap-3" style={{ alignItems: 'center' }}>
                <Avatar id={profile.avatar} size={56} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="wr-row gap-2" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <span className="h3" style={{ fontSize: 17 }}>{profile.displayName}</span>
                    {isAI && <Pill kind="accent">AI</Pill>}
                    {profile.isDemo && <Pill kind="ghost" style={{ fontSize: 9 }}>demo</Pill>}
                  </div>
                  {games > 0 && (
                    <div className="wr-text-3" style={{ fontSize: 12, marginTop: 2 }}>
                      {wins} W · {games} G · {games > 0 ? Math.round((wins / games) * 100) : 0}% win rate
                    </div>
                  )}
                </div>
              </div>

              {profile.bio && (
                <p style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 12, lineHeight: 1.45 }}>
                  {profile.bio}
                </p>
              )}

              {/* Quick actions */}
              <div className="wr-col gap-2 mt-4">
                {friendStatus === 'self' ? (
                  <Btn kind="ghost" full icon="User" onClick={() => { navigate('/profile'); onClose(); }}>
                    Edit your profile
                  </Btn>
                ) : isAI ? (
                  <Btn kind="primary" full icon="Bot" onClick={() => { navigate('/solo'); onClose(); }}>
                    Play vs. Atom
                  </Btn>
                ) : friendStatus === 'friends' ? (
                  <Btn kind="ghost" full icon="UserMinus" loading={busy} onClick={handleRemoveFriend}>
                    Friends · Remove
                  </Btn>
                ) : friendStatus === 'pending-out' ? (
                  <Btn kind="ghost" full icon="X" loading={busy} onClick={handleRemoveFriend}>
                    Cancel friend request
                  </Btn>
                ) : friendStatus === 'pending-in' ? (
                  <Btn kind="primary" full icon="Check" onClick={handleAddFriend}>
                    Accept friend request
                  </Btn>
                ) : !currentUser ? (
                  <Btn kind="ghost" full icon="LogIn" onClick={() => { navigate('/login'); onClose(); }}>
                    Sign in to add as friend
                  </Btn>
                ) : (
                  <Btn kind="primary" full icon="UserPlus" loading={busy} onClick={handleAddFriend}>
                    Add as friend
                  </Btn>
                )}
                <Btn
                  kind="ghost"
                  full
                  icon="ArrowRight"
                  onClick={() => { navigate(`/u/${profile.uid}`); onClose(); }}
                >
                  View full profile
                </Btn>
              </div>

              {/* SAFETY. Quiet, but present on every roster in the app — this
                  sheet is what opens when you tap anybody's face, which is the
                  only place a person will think to look for it in the moment.
                  Understated on purpose: it should be findable, not shouty. */}
              {canModerate && (
                <div
                  className="wr-row gap-2"
                  style={{
                    marginTop: 14, paddingTop: 12,
                    borderTop: '1px solid var(--line)',
                    justifyContent: 'center',
                  }}
                >
                  {confirmBlock ? (
                    <div className="wr-col gap-2" style={{ width: '100%' }}>
                      <div
                        className="wr-text-3"
                        style={{ fontSize: 12, textAlign: 'center', lineHeight: 1.4 }}
                      >
                        Block {profile.displayName}? They won't be able to join your
                        games. You can undo this in Settings.
                      </div>
                      <div className="wr-row gap-2">
                        <Btn kind="ghost" full onClick={() => setConfirmBlock(false)} disabled={busy}>
                          Cancel
                        </Btn>
                        <Btn kind="primary" full icon="Ban" loading={busy} onClick={handleBlock}>
                          Block
                        </Btn>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => setConfirmBlock(true)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          font: 'inherit', fontSize: 12, fontWeight: 600,
                          color: 'var(--ink-3)', padding: '8px 12px', minHeight: 36,
                        }}
                      >
                        Block
                      </button>
                      <span aria-hidden style={{ color: 'var(--line-2)', fontSize: 12 }}>·</span>
                      <button
                        type="button"
                        onClick={() => setReporting(true)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          font: 'inherit', fontSize: 12, fontWeight: 600,
                          color: 'var(--ink-3)', padding: '8px 12px', minHeight: 36,
                        }}
                      >
                        Report
                      </button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </motion.div>
      </motion.div>

      {reporting && profile && (
        <ReportDialog
          key="report"
          uid={profile.uid}
          name={profile.displayName}
          avatar={profile.avatar}
          contextType={contextType}
          contextId={contextId}
          onClose={() => setReporting(false)}
          onBlocked={() => onClose()}
        />
      )}
    </AnimatePresence>
  );
}
